import { useCallback, useEffect, useRef, useState } from "react";
import { api } from "../api";
import { useIsMobile } from "../useMedia";
import type { Meta, Patient, SessionUser } from "../types";
import PatientList from "./PatientList";
import PatientForm from "./PatientForm";
import CasesPanel from "./CasesPanel";
import { useMessage } from "./Message";

export interface Filters {
  page: number;
  query: string;
  /** Строка поиска — диагноз (ищется в документах), а не Ф.И.О., телефон… */
  diag: boolean;
  okt: boolean;
  doctor: string;
  month: number;
  year: number;
}

const EMPTY_FILTERS: Filters = {
  page: 1,
  query: "",
  diag: false,
  okt: false,
  doctor: "",
  month: 0,
  year: 0,
};

/**
 * engine/index.content.php — the patients section.
 *
 * The surrounding shell and the top menu moved to App/TopBar when the
 * operations register became a second section: both pages have to show the
 * same navigation, and one copy of it is the way to keep them the same.
 */
export default function PatientsPage({
  user,
  meta,
  extSearch = null,
  extAdd = 0,
  extPatient = null,
}: {
  user: SessionUser;
  meta: Meta;
  /** Поиск из мобильной шапки: TopBar собрал фильтры, страница применяет. */
  extSearch?: { f: Omit<Filters, "page">; n: number } | null;
  /** «+» из мобильной шапки — открыть форму нового пациента. */
  extAdd?: number;
  /** «Карточка пациента» из записи или оперсписка — открыть этого пациента. */
  extPatient?: { id: number; n: number } | null;
}) {
  const message = useMessage();
  // Below the breakpoint the two columns become one pane at a time: the list,
  // or — once a patient is picked — that patient's cases. Both stay mounted, so
  // each keeps its own scroll position (and the list keeps its lazy loader).
  const isMobile = useIsMobile();
  const [filters, setFilters] = useState<Filters>(EMPTY_FILTERS);
  const [patients, setPatients] = useState<Patient[]>([]);
  const [pages, setPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState<Patient | null>(null);
  const [editing, setEditing] = useState<Patient | "new" | null>(null);
  const [doctors, setDoctors] = useState<string[]>([]);
  const queryRef = useRef<HTMLInputElement | null>(null);
  /** В строке поиска что-то набрано (но, может, ещё не искали) — для «×». */
  const [typed, setTyped] = useState(false);
  /** Кнопка поиска перечитывает список даже с тем же запросом. */
  const [searchN, setSearchN] = useState(0);

  const [loadingMore, setLoadingMore] = useState(false);

  // Lazy scroll: a filter change reloads page 1 and replaces the list; nearing
  // the bottom of the left column appends the next page.
  // Ответ устаревшего запроса (фильтр сменили, пока шёл поиск) не показывается:
  // считается только последний.
  const loadSeq = useRef(0);
  const load = useCallback(async () => {
    const seq = ++loadSeq.current;
    setLoading(true);
    try {
      const res = await api.patients({ ...filters, page: 1 });
      if (seq !== loadSeq.current) return;
      setPatients(res.patients);
      setPages(res.pages);
      setTotal(res.total);
      setFilters((prev) => (prev.page === 1 ? prev : { ...prev, page: 1 }));
    } catch (err) {
      if (seq === loadSeq.current) message(err as Error);
    } finally {
      if (seq === loadSeq.current) setLoading(false);
    }
  }, [
    filters.query,
    filters.diag,
    filters.okt,
    filters.doctor,
    filters.month,
    filters.year,
    searchN,
    message,
  ]);

  useEffect(() => {
    load();
  }, [load]);

  const loadMore = useCallback(async () => {
    if (loading || loadingMore || filters.page >= pages) return;
    const nextPage = filters.page + 1;
    const seq = loadSeq.current;
    setLoadingMore(true);
    try {
      const res = await api.patients({ ...filters, page: nextPage });
      // Пока догружали страницу, фильтр сменился — эти строки уже не к месту.
      if (seq !== loadSeq.current) return;
      setPatients((prev) => {
        const seen = new Set(prev.map((p) => p.pat_id));
        return [...prev, ...res.patients.filter((p) => !seen.has(p.pat_id))];
      });
      setPages(res.pages);
      setTotal(res.total);
      setFilters((prev) => ({ ...prev, page: nextPage }));
    } catch (err) {
      message(err as Error);
    } finally {
      setLoadingMore(false);
    }
  }, [loading, loadingMore, filters, pages, message]);

  const onListScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const el = e.currentTarget;
    if (el.scrollTop + el.clientHeight >= el.scrollHeight - 400) loadMore();
  };

  useEffect(() => {
    api
      .doctors()
      .then((r) => setDoctors(r.doctors))
      .catch(() => setDoctors([]));
  }, []);

  const patch = (next: Partial<Filters>) =>
    setFilters((prev) => ({ ...prev, ...next, page: next.page ?? 1 }));

  // Толчки из мобильной шапки: применить собранный там поиск / открыть форму.
  useEffect(() => {
    if (!extSearch) return;
    patch({ ...extSearch.f, page: 1 });
    setSelected(null);
    if (queryRef.current) queryRef.current.value = String(extSearch.f.query ?? "");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [extSearch?.n]);

  useEffect(() => {
    if (extAdd > 0) setEditing("new");
  }, [extAdd]);

  // Переход «Карточка пациента» из другого раздела: пациента может не быть в
  // текущем списке (фильтры!), поэтому карточка тянется по id напрямую.
  useEffect(() => {
    if (!extPatient) return;
    api
      .patient(extPatient.id)
      .then(({ patient }) => {
        setSelected(patient);
        // В строку поиска — Ф.И.О., чтобы пациент оказался в списке и на виду.
        patch({ ...EMPTY_FILTERS, query: patient.fio, page: 1 });
        setTyped(true);
        if (queryRef.current) queryRef.current.value = patient.fio;
        focusRow.current = patient.pat_id;
      })
      .catch((err) => message(err as Error));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [extPatient?.n]);

  /** Строка пациента, к которой надо прокрутить после загрузки списка. */
  const focusRow = useRef<number | null>(null);
  useEffect(() => {
    if (loading || focusRow.current === null) return;
    if (!patients.some((p) => p.pat_id === focusRow.current)) return;
    focusRow.current = null;
    window.requestAnimationFrame(() => {
      document.querySelector(".pat-row.active")?.scrollIntoView({ block: "center" });
    });
  }, [loading, patients]);

  async function deletePatient(pat: Patient) {
    if (!confirm(`Удалить пациента «${pat.fio}»?`)) return;
    try {
      await api.deletePatient(pat.pat_id);
    } catch (err) {
      message(err as Error);
      return;
    }
    if (selected?.pat_id === pat.pat_id) setSelected(null);
    message("Пациент удалён");
    load();
  }

  return (
    <>
      <div className="content">
        <div className="inner">
          <div className="container">
            <div
              className={`inner-left scroll${isMobile && selected ? " pane-off" : ""}`}
              onScroll={onListScroll}
            >
              <div className="block">
                <div className="pat-filters">
                  <div className="pat-filters-left">
                    <div className="pat-filters-search">
                      <input
                        ref={queryRef}
                        type="text"
                        name="query"
                        placeholder={filters.diag ? "Введите диагноз" : "Введите критерий поиска"}
                        defaultValue={filters.query}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") patch({ query: e.currentTarget.value.trim() });
                        }}
                        onChange={(e) => setTyped(!!e.currentTarget.value)}
                      />
                      {/* «Диагноз» меняет смысл строки поиска: набранное ищется
                          в диагнозах документов пациента. Набранное, но ещё не
                          отправленное, уходит вместе с переключением — набрали
                          «глаукома», нажали «Диагноз», и поиск уже идёт. */}
                      <div
                        className={`pat-filters-okt pat-filters-diag${filters.diag ? " selected" : ""}`}
                        title="Искать набранное в диагнозах пациентов: осмотры, обоснования, выписки, протоколы ОКТ"
                        onClick={() =>
                          patch({ diag: !filters.diag, query: (queryRef.current?.value ?? "").trim() })
                        }
                      >
                        <input
                          type="checkbox"
                          name="query_diag"
                          checked={filters.diag}
                          readOnly
                          style={{ pointerEvents: "none" }}
                        />
                        Диагноз
                      </div>
                      {/* The whole pill toggles, with the legacy .selected
                          highlight — the bare checkbox was the only click
                          target before, and it is nearly invisible. */}
                      <div
                        className={`pat-filters-okt${filters.okt ? " selected" : ""}`}
                        onClick={() => patch({ okt: !filters.okt })}
                      >
                        <input
                          type="checkbox"
                          name="query_okt"
                          checked={filters.okt}
                          readOnly
                          style={{ pointerEvents: "none" }}
                        />
                        ОКТ
                      </div>
                      {/* Перенос строки фильтров на ноутбучной ширине — mobile.css. */}
                      <div className="pat-filters-break" />
                      <select
                        name="query_doc"
                        value={filters.doctor}
                        onChange={(e) => patch({ doctor: e.target.value })}
                      >
                        <option value="">Врач</option>
                        {doctors.map((d) => (
                          <option key={d} value={d}>
                            {d}
                          </option>
                        ))}
                      </select>
                      <select
                        name="query_month"
                        value={filters.month}
                        onChange={(e) => patch({ month: Number(e.target.value) })}
                      >
                        <option value={0}>Месяц</option>
                        {Object.entries(meta.months).map(([k, v]) => (
                          <option key={k} value={k}>
                            {v}
                          </option>
                        ))}
                      </select>
                      <select
                        name="query_year"
                        value={filters.year}
                        onChange={(e) => patch({ year: Number(e.target.value) })}
                      >
                        <option value={0}>Год</option>
                        {meta.years.map((y) => (
                          <option key={y} value={y}>
                            {y}
                          </option>
                        ))}
                      </select>
                      <div
                        className="button"
                        title="Найти"
                        onClick={() => {
                          patch({ query: (queryRef.current?.value ?? "").trim() });
                          // Даже с тем же запросом — перечитать список: кнопкой
                          // поиска обновляют страницу после чужих правок.
                          setSearchN((n) => n + 1);
                        }}
                      >
                        &nbsp;
                      </div>
                      {/* «×» показывается, только когда есть что сбрасывать:
                          текст или любой из фильтров. Чистит всё разом. */}
                      {(typed ||
                        filters.query ||
                        filters.diag ||
                        filters.okt ||
                        filters.doctor ||
                        filters.month !== 0 ||
                        filters.year !== 0) && (
                        <div
                          className="pat-filters-clear"
                          title="Очистить поиск и фильтры"
                          onClick={() => {
                            if (queryRef.current) queryRef.current.value = "";
                            setTyped(false);
                            patch({ query: "", diag: false, okt: false, doctor: "", month: 0, year: 0 });
                          }}
                        >
                          ×
                        </div>
                      )}
                      <div className="clear" />
                    </div>
                  </div>
                  <div className="pat-filters-right">
                    {user.can_add && (
                      <div className="button pat-add-button" onClick={() => setEditing("new")}>
                        Добавить
                      </div>
                    )}
                  </div>
                </div>

                <div className="pat-row pat-row-header">
                  <div className="col-1">Добавлен(а)</div>
                  <div className="col-2">Ф.И.О.</div>
                  <div className="col-3">Д/рождения</div>
                  <div className="col-4">Телефон</div>
                  <div className="col-5">Адрес регистрации</div>
                  <div className="col-6">Документы</div>
                  <div className="col-7" />
                </div>

                <PatientList
                  patients={patients}
                  meta={meta}
                  user={user}
                  loading={loading}
                  loadingMore={loadingMore}
                  hasMore={filters.page < pages}
                  selectedId={selected?.pat_id ?? null}
                  showOkt={filters.okt}
                  total={total}
                  // Искали диагноз как Ф.И.О. и ничего не нашли — предложить
                  // тот же запрос по диагнозам, не заставляя искать переключатель.
                  emptyHint={
                    !filters.diag && /[a-zа-яё]/i.test(filters.query) ? (
                      <span className="pat-empty-diag" onClick={() => patch({ diag: true })}>
                        Искать «{filters.query}» в диагнозах
                      </span>
                    ) : null
                  }
                  onSelect={setSelected}
                  onEdit={(p) => setEditing(p)}
                  onDelete={deletePatient}
                />
              </div>
            </div>

            <div className={`inner-right scroll${isMobile && !selected ? " pane-off" : ""}`}>
              {isMobile && selected && (
                <div className="pane-back" onClick={() => setSelected(null)}>
                  К списку пациентов
                </div>
              )}
              <div className="block">
                {selected ? (
                  <CasesPanel patient={selected} user={user} meta={meta} />
                ) : (
                  <div className="docs-no">
                    <div>Протоколы недоступны.</div>
                    Выберите пациента.
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {editing && (
        <PatientForm
          meta={meta}
          user={user}
          patient={editing === "new" ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={(saved) => {
            setEditing(null);
            message(editing === "new" ? "Пациент добавлен" : "Данные сохранены");
            // Новая карточка — сразу открыть; «открыть существующую» из
            // проверки на дубли — тоже она, а не вторая.
            if (saved && (editing === "new" || selected?.pat_id === saved.pat_id)) setSelected(saved);
            load();
          }}
        />
      )}
    </>
  );
}
